import React, { useEffect, useState } from "react";
import { collection, getDocs, doc, getDoc } from "firebase/firestore";
import { db } from "../config/firebase";
import PageTemplate from "../templates/PageTemplate";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { Card, CardHeader, CardContent, CardTitle } from "../components/Card";

const STATUS_LABELS = {
  pending: "En attente",
  confirmed: "Confirmé",
  waitPayment: "En attente paiement",
  paymentConfirmed: "Paiement confirmé",
};

const STATUS_COLORS = {
  pending: "bg-yellow-100 text-yellow-800 border-yellow-200",
  confirmed: "bg-blue-100 text-blue-800 border-blue-200",
  waitPayment: "bg-orange-100 text-orange-800 border-orange-200",
  paymentConfirmed: "bg-green-100 text-green-800 border-green-200",
};

export default function AppointmentsList() {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const navigate = useNavigate();

  useEffect(() => {
    async function fetch() {
      setLoading(true);
      const snap = await getDocs(collection(db, "appointments"));
      const data = [];
      for (const docSnap of snap.docs) {
        const appoint = { id: docSnap.id, ...docSnap.data() };

        // Fetch emails
        let userEmail = "";
        let nounouEmail = "";
        if (appoint.userId) {
          const parentSnap = await getDoc(doc(db, "users", appoint.userId));
          if (parentSnap.exists()) userEmail = parentSnap.data().email || "";
        }
        if (appoint.nounouId) {
          const nounouSnap = await getDoc(doc(db, "users", appoint.nounouId));
          if (nounouSnap.exists()) nounouEmail = nounouSnap.data().email || "";
        }
        data.push({
          ...appoint,
          userEmail,
          nounouEmail,
        });
      }
      setAppointments(data);
      setLoading(false);
    }
    fetch();
  }, []);

  const filteredAppointments =
    filter === "all"
      ? appointments
      : appointments.filter((a) => a.status === filter);

  const stats = {
    total: appointments.length,
    pending: appointments.filter((a) => a.status === "pending").length,
    confirmed: appointments.filter((a) => a.status === "confirmed").length,
    waitPayment: appointments.filter((a) => a.status === "waitPayment").length,
    paymentConfirmed: appointments.filter((a) => a.status === "paymentConfirmed")
      .length,
  };

  return (
    <PageTemplate
      loading={loading}
      content={
        <div className="space-y-6">
          {/* Info Card */}
          <Card className="bg-gradient-to-r from-pink-50 to-rose-50 border-pink-200">
            <CardContent className="flex items-start gap-4">
              <div className="p-3 bg-[#ff9be4] rounded-lg">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="w-6 h-6 text-white"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5"
                  />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">
                  Gestion des rendez-vous
                </h3>
                <p className="text-sm text-gray-600">
                  Consultez et gérez tous les rendez-vous entre parents et nounous.{" "}
                  {stats.total} rendez-vous au total.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <Card
              className={`cursor-pointer transition-all ${
                filter === "all"
                  ? "ring-2 ring-[#ff9be4] bg-pink-50"
                  : "hover:shadow-md"
              }`}
              onClick={() => setFilter("all")}
            >
              <CardContent className="text-center">
                <p className="text-sm font-medium text-gray-600">Total</p>
                <p className="text-3xl font-bold text-[#ff9be4] mt-1">
                  {stats.total}
                </p>
              </CardContent>
            </Card>

            <Card
              className={`cursor-pointer transition-all ${
                filter === "pending"
                  ? "ring-2 ring-yellow-400 bg-yellow-50"
                  : "hover:shadow-md"
              }`}
              onClick={() => setFilter("pending")}
            >
              <CardContent className="text-center">
                <p className="text-sm font-medium text-gray-600">En attente</p>
                <p className="text-3xl font-bold text-yellow-600 mt-1">
                  {stats.pending}
                </p>
              </CardContent>
            </Card>

            <Card
              className={`cursor-pointer transition-all ${
                filter === "confirmed"
                  ? "ring-2 ring-blue-400 bg-blue-50"
                  : "hover:shadow-md"
              }`}
              onClick={() => setFilter("confirmed")}
            >
              <CardContent className="text-center">
                <p className="text-sm font-medium text-gray-600">Confirmés</p>
                <p className="text-3xl font-bold text-blue-600 mt-1">
                  {stats.confirmed}
                </p>
              </CardContent>
            </Card>

            <Card
              className={`cursor-pointer transition-all ${
                filter === "waitPayment"
                  ? "ring-2 ring-orange-400 bg-orange-50"
                  : "hover:shadow-md"
              }`}
              onClick={() => setFilter("waitPayment")}
            >
              <CardContent className="text-center">
                <p className="text-sm font-medium text-gray-600">En paiement</p>
                <p className="text-3xl font-bold text-orange-600 mt-1">
                  {stats.waitPayment}
                </p>
              </CardContent>
            </Card>

            <Card
              className={`cursor-pointer transition-all ${
                filter === "paymentConfirmed"
                  ? "ring-2 ring-green-400 bg-green-50"
                  : "hover:shadow-md"
              }`}
              onClick={() => setFilter("paymentConfirmed")}
            >
              <CardContent className="text-center">
                <p className="text-sm font-medium text-gray-600">Payés</p>
                <p className="text-3xl font-bold text-green-600 mt-1">
                  {stats.paymentConfirmed}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Appointments Table */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>
                  {filter === "all"
                    ? "Tous les rendez-vous"
                    : `Rendez-vous - ${STATUS_LABELS[filter]}`}
                </CardTitle>
                <span className="text-sm text-gray-500">
                  {filteredAppointments.length} rendez-vous
                </span>
              </div>
            </CardHeader>
            <CardContent>
              {filteredAppointments.length === 0 ? (
                <div className="text-center py-12">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="w-16 h-16 text-gray-300 mx-auto mb-4"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5"
                    />
                  </svg>
                  <p className="text-gray-600 font-medium mb-2">
                    Aucun rendez-vous trouvé
                  </p>
                  <p className="text-sm text-gray-500">
                    {filter === "all"
                      ? "Aucun rendez-vous n'a été créé pour le moment"
                      : `Aucun rendez-vous avec le statut "${STATUS_LABELS[filter]}"`}
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Date & Heure
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Parent
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Nounou
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Statut
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredAppointments.map((a) => (
                        <tr
                          key={a.id}
                          className="hover:bg-gray-50 transition-colors"
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth={1.5}
                                stroke="currentColor"
                                className="w-5 h-5 text-gray-400"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5m-9-6h.008v.008H12v-.008ZM12 15h.008v.008H12V15Zm0 2.25h.008v.008H12v-.008ZM9.75 15h.008v.008H9.75V15Zm0 2.25h.008v.008H9.75v-.008ZM7.5 15h.008v.008H7.5V15Zm0 2.25h.008v.008H7.5v-.008Zm6.75-4.5h.008v.008h-.008v-.008Zm0 2.25h.008v.008h-.008V15Zm0 2.25h.008v.008h-.008v-.008Zm2.25-4.5h.008v.008H16.5v-.008Zm0 2.25h.008v.008H16.5V15Z"
                                />
                              </svg>
                              <div>
                                <p className="text-sm font-medium text-gray-900">
                                  {a.date
                                    ? moment(a.date).format("DD/MM/YYYY")
                                    : "-"}
                                </p>
                                <p className="text-xs text-gray-500">{a.time}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth={1.5}
                                stroke="currentColor"
                                className="w-5 h-5 text-gray-400"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75"
                                />
                              </svg>
                              <p className="text-sm text-gray-900">
                                {a.userEmail || "-"}
                              </p>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth={1.5}
                                stroke="currentColor"
                                className="w-5 h-5 text-gray-400"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75"
                                />
                              </svg>
                              <p className="text-sm text-gray-900">
                                {a.nounouEmail || "-"}
                              </p>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${
                                STATUS_COLORS[a.status] ||
                                "bg-gray-100 text-gray-800 border-gray-200"
                              }`}
                            >
                              {STATUS_LABELS[a.status] || a.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <button
                              onClick={() => navigate(`/rdv/${a.id}`)}
                              className="inline-flex items-center gap-2 text-[#ff9be4] hover:text-[#ff6bd4] font-medium transition-colors"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth={1.5}
                                stroke="currentColor"
                                className="w-4 h-4"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z"
                                />
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
                                />
                              </svg>
                              Détails
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      }
    />
  );
}